import { Meter } from "@/components";
import { Decimal } from "@/lib/decimal";
import { formatGrams, formatNumber } from "@/lib/format";
import type { Lot } from "../api";
import styles from "./StockMeter.module.css";

const LOW_FRACTION = "0.2";

/** What's left in a lot against what the full container held: "12.300 g of 25 g". A lot still on
 * order holds nothing yet, so it says what's coming instead: "10 g on order". */
export function StockMeter({
  lot,
  size = "sm",
  hideLabel = true,
}: {
  lot: Pick<Lot, "balance_g" | "initial_g" | "code" | "material" | "status">;
  size?: "sm" | "md";
  hideLabel?: boolean;
}) {
  if (lot.status === "on_order") {
    return (
      <span className={styles.onOrder}>
        {lot.initial_g ? `${formatNumber(lot.initial_g, { maxDecimals: 3 })} g on order` : "On order"}
      </span>
    );
  }
  const balance = Decimal.tryFrom(lot.balance_g) ?? Decimal.ZERO;
  const initial = Decimal.tryFrom(lot.initial_g);
  const max = initial && !initial.isZero() ? initial : balance.isNegative() || balance.isZero() ? Decimal.ONE : balance;
  const valueText = initial
    ? `${formatGrams(lot.balance_g)} g of ${formatNumber(lot.initial_g, { maxDecimals: 3 })} g`
    : `${formatGrams(lot.balance_g)} g`;
  return (
    <Meter
      label={`Stock left in ${lot.material.name} (${lot.code})`}
      hideLabel={hideLabel}
      value={balance.toString()}
      max={max.toString()}
      low={max.times(LOW_FRACTION).toString()}
      level={lot.status === "disposed" || balance.compare(0) <= 0 ? "empty" : undefined}
      valueText={valueText}
      size={size}
    />
  );
}
